// Bidi base direction for a chat composer field. Mirrors the native fix
// (composerBaseRtl on Android / composerBaseIsRTL on iOS, #6353/#6396/#6397):
// follow the direction of what's being TYPED (first strong character), not
// the page/UI language, so Hebrew/Arabic typed in an English UI right-aligns
// with the caret on the correct side, and vice-versa.
//
// `dir="auto"` looks like the right built-in tool, but browsers resolve it
// from the field's VALUE only — while the field is empty it falls back to
// plain "ltr" rather than the page language, so a mixed-language placeholder
// (e.g. Hebrew "בקש מ-{agentName} לטפל במשהו…" with a Latin agent name) gets
// bidi-reordered as an RTL run embedded in an LTR paragraph, scrambling word
// order. Resolving direction ourselves and falling back to the UI language
// (via `<html dir>`, stamped by pages/_app.tsx) keeps the placeholder in its
// own correctly-based paragraph instead.
//
// RTL is an explicit scalar-range check (Hebrew/Arabic + related RTL blocks).
// LTR is "any Unicode letter that isn't RTL" via the `\p{L}` Unicode property
// escape — NOT an ASCII/Latin-only check, which would regress Cyrillic/Greek/
// CJK/etc. content to the empty-string fallback (in an RTL UI that means those
// composers/placeholders incorrectly resolve to RTL until a Latin letter
// appears). `\p{L}` is a basic, engine-native Unicode property test
// (V8/JSC/SpiderMonkey all implement it consistently), so it doesn't carry the
// platform-divergence risk a bidi-category API would.
const RTL_CHAR = /[֑-߿יִ-﷿ﹰ-﻿]/;
const LETTER_CHAR = /\p{L}/u;

function firstStrongIsRtl(text: string): boolean | null {
  for (const ch of text) {
    if (RTL_CHAR.test(ch)) return true;
    if (LETTER_CHAR.test(ch)) return false;
  }
  return null;
}

export function composerDir(text: string): 'rtl' | 'ltr' {
  const strong = firstStrongIsRtl(text);
  if (strong !== null) return strong ? 'rtl' : 'ltr';
  const uiRtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
  return uiRtl ? 'rtl' : 'ltr';
}
